import { useLanguage } from "@/i18n/LanguageContext";
import { Layers, Target, KeyRound, MapPin } from "lucide-react";

export const SeoContent = () => {
  const { t } = useLanguage();

  // We render translation keys as arrays/objects defined in translations.js (seo.*)
  const features = t("seo.features.items") || [];
  const compositionLayers = t("seo.composition.layers") || [];
  const applications = t("seo.applications.items") || [];
  const supports = t("seo.supports.items") || [];

  return (
    <section
      id="film-pdlc"
      data-testid="seo-content-section"
      className="relative py-20 sm:py-28 lg:py-36 bg-[#0a0a0a] text-[#cbc1ad] overflow-hidden"
      itemScope
      itemType="https://schema.org/Product"
    >
      <meta itemProp="name" content="Film LCD / PDLC ON/OFF — Smart Film à cristaux liquides" />
      <meta itemProp="brand" content="smartxglass" />
      <meta itemProp="countryOfOrigin" content="CH" />

      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] hairline" />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <header className="mb-14 sm:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#b87333]/30 bg-[#b87333]/5 mb-6">
            <MapPin className="w-3.5 h-3.5 text-[#d89557]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#d89557]">
              {t("seo.swissBadge")}
            </span>
          </div>

          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#f4ede0] leading-[1.1]">
            {t("seo.h2.line1")} <span className="copper-text italic">{t("seo.h2.line2")}</span>
          </h2>
          <p className="mt-6 max-w-3xl text-sm sm:text-base leading-relaxed text-[#cbc1ad]">
            {t("seo.intro")}
          </p>
        </header>

        {/* Supports */}
        <article className="mb-14 sm:mb-20">
          <h3 className="font-display text-xl sm:text-2xl text-[#f4ede0] mb-5">
            {t("seo.supports.title")}
          </h3>
          <p className="text-sm text-[#cbc1ad] mb-5 max-w-3xl leading-relaxed">
            {t("seo.supports.lead")}
          </p>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
            {supports.map((item) => (
              <li
                key={item}
                className="px-3 py-2 border border-[#2a2422] bg-[#0d0c0b] text-[#cbc1ad] hover:border-[#b87333]/50 hover:text-[#f4ede0] transition"
              >
                {item}
              </li>
            ))}
          </ul>
        </article>

        {/* Two-column: Features / Composition */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 mb-14 sm:mb-20">
          {/* Features */}
          <article className="glass-card p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-5">
              <KeyRound className="w-4 h-4 text-[#d89557]" />
              <h3 className="font-display text-xl sm:text-2xl text-[#f4ede0]">
                {t("seo.features.title")}
              </h3>
            </div>
            <ul className="space-y-3 text-sm">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <span className="mt-2 h-1 w-3 bg-[#b87333] flex-shrink-0" />
                  <span className="text-[#cbc1ad] leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </article>

          {/* Composition */}
          <article className="glass-card p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-5">
              <Layers className="w-4 h-4 text-[#d89557]" />
              <h3 className="font-display text-xl sm:text-2xl text-[#f4ede0]">
                {t("seo.composition.title")}
              </h3>
            </div>
            <p className="text-sm text-[#cbc1ad] mb-5 leading-relaxed">
              {t("seo.composition.lead")}
            </p>
            <ol className="space-y-2 text-sm">
              {compositionLayers.map((layer, i) => (
                <li
                  key={layer}
                  className="flex items-center gap-3 px-3 py-2 border border-[#2a2422] bg-[#0d0c0b]"
                >
                  <span className="font-display text-[#d89557] text-xs w-6">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[#cbc1ad]">{layer}</span>
                </li>
              ))}
            </ol>
            <p className="text-xs text-[#7a716a] mt-5 leading-relaxed">
              {t("seo.composition.footer")}
            </p>
          </article>
        </div>

        {/* Applications */}
        <article>
          <div className="flex items-center gap-3 mb-5">
            <Target className="w-4 h-4 text-[#d89557]" />
            <h3 className="font-display text-xl sm:text-2xl text-[#f4ede0]">
              {t("seo.applications.title")}
            </h3>
          </div>
          <p className="text-sm text-[#cbc1ad] mb-6 max-w-3xl leading-relaxed">
            {t("seo.applications.lead")}
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            {applications.map((app, i) => (
              <li
                key={i}
                className="px-4 py-3 border border-[#2a2422] bg-[#0d0c0b] text-[#cbc1ad] hover:border-[#b87333]/50 hover:text-[#f4ede0] transition"
              >
                {app}
              </li>
            ))}
          </ul>
        </article>

        {/* Swiss-supplier reassurance band */}
        <div className="mt-16 sm:mt-20 p-6 sm:p-8 border border-[#b87333]/30 bg-[#b87333]/5 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
          <div className="flex-shrink-0 w-12 h-12 border border-[#b87333]/40 flex items-center justify-center">
            <span className="font-display text-[#d89557] text-xl">CH</span>
          </div>
          <div className="flex-1">
            <p className="text-sm sm:text-base text-[#f4ede0] font-medium">
              {t("seo.swissBlock.title")}
            </p>
            <p className="text-xs sm:text-sm text-[#cbc1ad] mt-1 leading-relaxed">
              {t("seo.swissBlock.desc")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
